import images from '@assets/images/index'
import { AvatarSelector } from '@components/AvataarsPicker'
import type { AvatarData } from '@components/AvataarsPicker/types'
import { Button } from '@components/ui'
import { useProfile } from '@hooks/useProfile'
import { Col, Flex, Form, Input, Row, message } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useAuth } from '@suntex.fim/auth/react'
import { LoginMode } from '@suntex.fim/enums'
import { getFlags } from '@suntex.fim/flags'
import { LoaderOverlay, Modal, showErrorMessage } from '@suntex.fim/ui'

/**
 * Constants for the MyAccount component
 */
const CONSTANTS = {
  LOGIN_MODE_KEY: 'login_mode',
  MODAL_WIDTH: 1062,
  FORM_PADDING: '24px 0',
  GUTTER_SIZE: 20,
  GRID_SIZES: {
    LARGE: 12,
    SMALL: 24
  }
} as const

/**
 * Types for the component
 */
interface LoginModeOption {
  key: LoginMode
  title: string
  description: string[]
  buttonText: string
}

type InputField = 'displayName' | 'contactName'

/**
 * MyAccount component for user profile management
 *
 * Features:
 * - Avatar selection and customization
 * - Profile information editing (display name, contact name)
 * - Login mode configuration (auto/manual)
 * - Real-time validation and change detection
 * - Optimized performance with memoization
 *
 * @returns JSX.Element The MyAccount component
 */
function MyAccount(): JSX.Element {
  // Component state
  const [displayName, setDisplayName] = useState<string>('')
  const [contactName, setContactName] = useState<string>('')
  const [loginMode, setLoginMode] = useState<LoginModeOption['key']>(
    LoginMode.Manual
  )
  const [initialLoginMode, setInitialLoginMode] = useState<
    LoginModeOption['key']
  >(LoginMode.Manual)
  const [isEdited, setIsEdited] = useState<boolean>(false)
  const [showLoginModeModal, setShowLoginModeModal] = useState<boolean>(false)
  const [localStorageLoginMode, setLocalStorageLoginMode] = useState<
    string | null
  >(null)
  const [currentAvatar, setCurrentAvatar] = useState<AvatarData | null>(null)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const { login: user, refresh: refetchUser } = useAuth()
  const {
    loginProfile,
    avatar: profileAvatar,
    initialAvatar,
    isLoading,
    getLoginProfile,
    updateProfile
  } = useProfile()

  // Memoized initial values to prevent unnecessary re-renders
  const initialValues = useMemo(
    () => ({
      displayName: loginProfile?.displayName ?? '',
      contactName: loginProfile?.contactName ?? ''
    }),
    [loginProfile]
  )

  // Memoized login mode options for the modal
  const loginModeOptions = useMemo<LoginModeOption[]>(
    () => [
      {
        key: LoginMode.Auto,
        title: 'Automatic Login',
        description: [
          'First In Math can remember you on this computer and log you in automatically.',
          "When you're done using First In Math, just close the browser window or tab and we will remember your login on this machine.",
          "This is convenient, but please don't do this if you share this computer with other people."
        ],
        buttonText: "I'd like to login automatically"
      },
      {
        key: LoginMode.Manual,
        title: 'Manual Login',
        description: [
          'You can log in to First In Math manually.',
          'You can use these credentials to log in to your account on other devices.',
          'You should choose this option if you share this computer with other people.'
        ],
        buttonText: "I'd like to login manually"
      }
    ],
    []
  )

  /**
   * Initialize component data on mount
   */
  useEffect(() => {
    const initializeData = async (): Promise<void> => {
      try {
        await getLoginProfile()
        const loginToken = localStorage.getItem(CONSTANTS.LOGIN_MODE_KEY)
        setLocalStorageLoginMode(loginToken)
        if (loginToken !== null) {
          setLoginMode(LoginMode.Auto)
          setInitialLoginMode(LoginMode.Auto)
        } else {
          setInitialLoginMode(LoginMode.Manual)
        }
      } catch (error) {
        showErrorMessage('Failed to initialize profile data', error)
      }
    }

    void initializeData()
  }, [getLoginProfile])

  /**
   * Update form values when profile data loads
   */
  useEffect(() => {
    setDisplayName(initialValues.displayName)
    setContactName(initialValues.contactName)
    setIsEdited(false)
  }, [initialValues])

  /**
   * Sync avatar when profile avatar changes
   */
  useEffect(() => {
    if (profileAvatar) {
      setCurrentAvatar(profileAvatar)
    }
  }, [profileAvatar])

  /**
   * Utility function to check if avatar has changed
   */
  const hasAvatarChanged = useCallback(
    (newAvatar: AvatarData | null): boolean => {
      return JSON.stringify(newAvatar) !== JSON.stringify(initialAvatar)
    },
    [initialAvatar]
  )

  /**
   * Handler for avatar changes with validation
   * This code is commented because client asked to remove this functionality as of now
   */
  const handleAvatarChange = useCallback(
    (newAvatar: AvatarData) => {
      setCurrentAvatar(newAvatar)
      if (hasAvatarChanged(newAvatar)) {
        setIsEdited(true)
      }
    },
    [hasAvatarChanged]
  )

  /**
   * Handler for input field changes with validation
   */
  const handleInputChange = useCallback(
    (field: InputField) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setIsEdited(true)

      if (field === 'displayName') {
        setDisplayName(value)
      } else if (field === 'contactName') {
        setContactName(value)
      }
    },
    []
  )

  /**
   * Optimized save handler with error handling
   */
  const handleSave = useCallback(async (): Promise<void> => {
    if (!loginProfile || !user) {
      message.error('Profile or user data not available')
      return
    }

    setIsSaving(true)

    try {
      const avatarChanged = hasAvatarChanged(currentAvatar)
      const displayNameChanged = displayName !== initialValues.displayName
      const contactNameChanged = contactName !== initialValues.contactName
      const loginModeChanged =
        (loginMode === LoginMode.Auto && localStorageLoginMode === null) ||
        (loginMode === LoginMode.Manual && localStorageLoginMode !== null)

      const success = await updateProfile({
        displayName: displayNameChanged ? displayName : undefined,
        contactName: contactNameChanged ? contactName : undefined,
        avatar: avatarChanged ? (currentAvatar ?? undefined) : undefined,
        loginMode: loginModeChanged ? loginMode : undefined,
        localStorageLoginMode,
        userId: user.userId,
        contactId: user.contactId
      })

      if (success) {
        void refetchUser()
        setIsEdited(false)
        // Update the initial login mode to the new current mode after successful save
        setInitialLoginMode(loginMode)
      } else {
        message.error('Failed to update profile')
      }
    } catch (error) {
      showErrorMessage('An error occurred while updating your profile', error)
    } finally {
      setIsSaving(false)
    }
  }, [
    loginProfile,
    user,
    displayName,
    contactName,
    currentAvatar,
    hasAvatarChanged,
    loginMode,
    localStorageLoginMode,
    initialValues,
    updateProfile
  ])

  /**
   * Wrapper for save handler to handle async operations in onClick
   */
  const handleSaveClick = useCallback(() => {
    void handleSave()
  }, [handleSave])

  /**
   * Handler for login mode modal confirmation
   */
  const handleLoginModeModalOk = useCallback(() => {
    const loginModeChanged = loginMode !== initialLoginMode

    // Set isEdited to true if there are any changes (either existing changes or login mode changes)
    if (loginModeChanged || isEdited) {
      setIsEdited(true)
    }

    setShowLoginModeModal(false)
  }, [loginMode, initialLoginMode, isEdited])

  /**
   * Handler for login mode changes with validation
   */
  const handleLoginModeChange = useCallback(
    (mode: LoginMode) => {
      setLoginMode(mode)
      // Mark as edited if the mode is different from the initial mode
      const loginModeChanged = mode !== initialLoginMode
      if (loginModeChanged) {
        setIsEdited(true)
      }
    },
    [initialLoginMode]
  )

  /**
   * Render login mode option in modal
   */
  const renderLoginModeOption = useCallback(
    (option: LoginModeOption) => (
      <Col
        key={option.key}
        className="gutter-row"
        span={12}
        onClick={() => handleLoginModeChange(option.key)}
      >
        <Flex
          vertical
          gap={5}
          justify="space-between"
          style={{
            backgroundColor:
              loginMode === option.key
                ? 'rgba(63, 178, 255, 0.05)'
                : 'transparent',
            transition: 'background-color 0.3s ease'
          }}
          className="my-account-modal__content-col"
        >
          <Flex vertical gap={5}>
            {option.key === LoginMode.Manual && loginProfile ? (
              <>
                <p>
                  You can log in to First In Math <strong>manually.</strong>
                </p>
                <p>
                  Your user id and password credentials are:{' '}
                  {loginProfile.userId}/
                  <span style={{ cursor: 'pointer' }} className="password-mask">
                    <span className="password-masked">
                      {loginProfile.password.replace(/./g, '*')}
                    </span>
                    <span className="password-reveal">
                      {loginProfile.password}
                    </span>
                  </span>{' '}
                  (Hover the mouse cursor over the hidden password to reveal
                  it.)
                </p>
                <p>
                  Instead of your user id, you can also use your email address:
                </p>
                <p className="fw-bold">{loginProfile.email}</p>
                <p>Please save this information in a secure place!</p>
                <p>
                  You can also use these credentials to log in to your account
                  on other devices.
                </p>
                <p className="fw-bold">
                  You should choose this option if you share this computer with
                  other people.
                </p>
              </>
            ) : (
              option.description.map((desc, index) => (
                <p
                  key={index}
                  className={
                    index === option.description.length - 1 ? 'fw-bold' : ''
                  }
                >
                  {desc}
                </p>
              ))
            )}
          </Flex>
          <Button
            intent="secondary"
            onClick={() => handleLoginModeChange(option.key)}
          >
            {option.buttonText}
          </Button>
        </Flex>
      </Col>
    ),
    [loginMode, loginProfile, handleLoginModeChange]
  )

  return (
    <>
      <LoaderOverlay isLoading={isLoading || isSaving} />
      <Flex justify="space-between" className="my-account">
        <h3>My Account</h3>
        <div>
          <Button
            intent="primary"
            size="small"
            disabled={!isEdited || isSaving}
            onClick={handleSaveClick}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </Flex>
      <Form layout="vertical">
        <Row gutter={CONSTANTS.GUTTER_SIZE} className="my-account__form">
          {getFlags().avatarSelector && (
            <Col span={CONSTANTS.GRID_SIZES.SMALL}>
              <div style={{ padding: CONSTANTS.FORM_PADDING }}>
                <AvatarSelector
                  currentAvatar={currentAvatar || undefined}
                  onSelect={handleAvatarChange}
                />
              </div>
            </Col>
          )}

          {/* Display Name */}
          <Col lg={CONSTANTS.GRID_SIZES.LARGE} sm={CONSTANTS.GRID_SIZES.SMALL}>
            <Form.Item colon={false} label="Display Name">
              <Input
                value={displayName}
                onChange={handleInputChange('displayName')}
                placeholder="Enter your display name"
                maxLength={50}
              />
            </Form.Item>
          </Col>

          {/* Contact Name */}
          <Col lg={CONSTANTS.GRID_SIZES.LARGE} sm={CONSTANTS.GRID_SIZES.SMALL}>
            <Form.Item colon={false} label="Contact Name">
              <Input
                value={contactName}
                onChange={handleInputChange('contactName')}
                placeholder="Enter your contact name"
                maxLength={50}
              />
            </Form.Item>
          </Col>

          {/* User ID */}
          <Col lg={CONSTANTS.GRID_SIZES.LARGE} sm={CONSTANTS.GRID_SIZES.SMALL}>
            <Form.Item colon={false} label="User ID">
              <Input
                value={loginProfile?.userId ?? ''}
                disabled
                placeholder="User ID will appear here"
              />
            </Form.Item>
          </Col>

          {/* Login Mode */}
          <Col lg={CONSTANTS.GRID_SIZES.LARGE} sm={CONSTANTS.GRID_SIZES.SMALL}>
            <Form.Item colon={false} label="Login Mode">
              <Input
                value={loginMode}
                disabled
                suffix={
                  <Button
                    intent="icon"
                    aria-label="Edit login mode settings"
                    onClick={() => setShowLoginModeModal(true)}
                    className="my-account-edit-button"
                    icon={<images.Pencil />}
                  />
                }
              />
            </Form.Item>
          </Col>

          {/* Email ID */}
          <Col lg={CONSTANTS.GRID_SIZES.LARGE} sm={CONSTANTS.GRID_SIZES.SMALL}>
            <Form.Item colon={false} label="Email ID">
              <Input
                value={loginProfile?.email ?? ''}
                disabled
                placeholder="Email will appear here"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {/* Login Mode Modal */}
      <Modal
        title="Important Account Information"
        open={showLoginModeModal}
        onOk={handleLoginModeModalOk}
        onCancel={() => setShowLoginModeModal(false)}
        footer={[
          <Button key="ok" intent="primary" onClick={handleLoginModeModalOk}>
            OK
          </Button>
        ]}
        width={CONSTANTS.MODAL_WIDTH}
        closable={false}
      >
        <Flex vertical gap={20} className="my-account-modal">
          <strong>
            Please read. This is very important! How would you like to log into
            First In Math?
          </strong>
          <Row gutter={16} className="my-account-modal__content">
            {loginModeOptions.map((option) => renderLoginModeOption(option))}
          </Row>
        </Flex>
      </Modal>
    </>
  )
}

export default MyAccount
